/* Foreverings wishlist - storefront core (2026-10-22, replaces Swish / Wishlist King).
   Loaded on every page from snippets/fe-wishlist-config.liquid (deferred). Talks to our own
   Worker through the Shopify app proxy (config.endpoint, e.g. /apps/wishlist-new).

   Two kinds of row share one list (regression fixes, 2026-10-22):
   - hearts (source "heart"): a heart on a card or buy box. The heart's filled state, add and remove
     only ever touch heart rows.
   - saved cart lines (source "cart"): a cart line parked by "Save for later", with its size,
     quantity, engraving and Perfect Fit Promise ("__pfp"). Only these appear in the cart page and
     drawer "Saved for later" strips, exactly like the old browser-only strip. Un-hearting a ring
     never deletes a saved line. Every row has a server key (product:variant:source:fingerprint).

   What lives here:
   - identity: a random guest id in localStorage ("fe-wl-guest"); when the shopper is logged in
     Shopify signs their customer id into every proxy request and the guest id is merged away
   - the list, cached in localStorage ("fe-wl-cache") so hearts paint before the network; other tabs
     pick changes up through the storage event
   - hearts: any element with data-fe-wl-heart + data-product-id + data-handle (+ data-title,
     data-price for analytics). A buy-box heart also carries data-fe-sync (hidden variant input) and
     data-fe-choice (the size select): when that select exists, the variant is bound only once it
     holds a value; when it does not exist (a single-variant product), the bound variant is used
   - the header heart (.wishlist-header-link) count + link
   - analytics: the same dataLayer "add_to_wishlist" event Swish pushed (GTM forwards it), plus
     Klaviyo "Added/Removed product from wishlist"; saved cart lines send nothing (the old strip didn't)
   - one-time imports: the shopper's Swish list (dual run; once per list on the server) and the old
     browser-only "Save for later" lines (fe-saved-later). Each is marked done only when it worked. The
     Swish mark is per identity (guest, or this customer), so a logged-out first visit never blocks the
     copy of the shopper's logged-in Swish list.
   - the strips' first paint: the last-built strip lines are kept in localStorage ("fe-wl-lines") so the
     cart page and the drawer draw them at once, as the old browser-only strip did; cartLinesNow() gives
     them from the current list, cartLines() rebuilds them from the network
   - moved rows: a saved line moved to the cart whose removal from the list failed is kept out of view
     ("fe-wl-gone") and its removal retried, so it is never offered again as if it had not moved
   Round 4 (2026-10-22): the caches and the moved-rows record carry who wrote them (WHO: this customer,
   or a guest) and are ignored for anyone else; tabs share the moved-rows record and the strip drawings;
   a failed call puts back only what it took out; the strips drop a line that left the list while they
   were loading; a guest Swish copy never runs in a browser where a customer copy already ran.
   Round 5: a tab whose server answers for someone else (a login, logout or account switch in another
   tab) writes no caches; a retry clears only the moved-row entry it was started for; Klaviyo "Removed"
   goes out only when the server deleted a row (removeLine resolves to { removed }).
   Round 6: answers are ordered by the list's version (the Worker bumps it on every change), so an
   older answer is never drawn over a newer one; for saved cart lines the shopper's latest action on
   each row (save or remove) is recorded ("fe-wl-intent") and, once every request for that row has
   settled, checked against the newest answer and sent once more if a request that overlapped it won.
   Round 7: answers are ordered per list (the Worker names the list, so a replaced guest list starts a
   new order); the shared cache carries the version and is never overwritten by an older one; a removal
   tells the Worker which version the shopper was looking at ("base"), and the Worker keeps any row
   saved after it, so a slow removal can never delete a later save; checks read only the keys the
   server sent (never this page's guesses); a check after a failed request first asks for a fresh list,
   and an action whose re-send failed stays stored for the next page.
   Round 8: a base travels with the list it was read from (the Worker ignores it for any other list,
   e.g. after a login merge); a removal the Worker kept because of a later save settles instead of
   being re-sent; a check never re-sends when another tab recorded a newer action on the same line;
   a cache copy also orders the tab's first answer.
   Final review: this page's changes still on their way (a heart just tapped, a row just removed) stay
   on top of every answer until their own answer lands, so hearts and the count move once per tap; the
   un-heart event carries the handle; the buy-box heart's GA value is the chosen variant's price; the
   old browser-only saved lines stay in localStorage after their import (a theme rollback still shows
   them) and lines added there later are imported too.

   Public API (window.FEWishlist): ready, state, has(productId), add(item, meta), remove(productId,
   variantId?), removeLine(key, opts), changeVariant(key, toVariantId), clear(), share(), refresh(),
   cartLines(limit), cartLinesNow(limit), money(cents), fetchProduct(handle), productUrl(item), onChange(fn).
   Change event: document "fe:wishlist:change" (detail = state). */
(function () {
  'use strict';
  if (window.FEWishlist) return;

  var cfgNode = document.getElementById('fe-wl-config');
  var cfg = {};
  try { cfg = JSON.parse(cfgNode ? cfgNode.textContent : '{}') || {}; } catch (e) { cfg = {}; }
  var ENDPOINT = cfg.endpoint || '/apps/wishlist';
  var IS_CUSTOMER = !!cfg.customer;
  var GUEST_KEY = 'fe-wl-guest', CACHE_KEY = 'fe-wl-cache', OLD_SAVED = 'fe-saved-later';
  var IMP_SAVED = 'fe-wl-imp-saved';
  var IMP_SWISH = 'fe-wl-imp-swish:' + (IS_CUSTOMER ? 'c' + (cfg.customer_id || '') : 'g');
  var LINES_KEY = 'fe-wl-lines', GONE_KEY = 'fe-wl-gone', GONE_DAYS = 7;
  /* who this browser is right now; caches written for someone else are not shown */
  var WHO = IS_CUSTOMER ? 'c' + (cfg.customer_id || '') : 'g';
  /* the same identity as the Worker states it in every answer ("g", or "c" + FNV-1a of the id) */
  function fnv(text) {
    var h = 0x811c9dc5;
    for (var i = 0; i < text.length; i++) { h ^= text.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
    return ('0000000' + h.toString(16)).slice(-8);
  }
  var WHO_SERVER = IS_CUSTOMER ? 'c' + fnv(String(cfg.customer_id || '')) : 'g';
  var foreign = false; /* the server answered for someone else: this tab writes no caches */
  var INTENT_KEY = 'fe-wl-intent';
  ls(function (s) { s.removeItem('fe-wl-resave'); }); /* round 5's queue, replaced by intents */

  var state = { items: [], count: 0, loaded: false, customer: IS_CUSTOMER, shared_token: null };
  var listeners = [];

  function ls(fn) { try { return fn(window.localStorage); } catch (e) { return null; } }
  function guestId(create) {
    var id = ls(function (s) { return s.getItem(GUEST_KEY); });
    if (id && /^[0-9a-f]{32}$/.test(id)) return id;
    if (!create) return null;
    var buf = new Uint8Array(16);
    (window.crypto || window.msCrypto).getRandomValues(buf);
    id = Array.prototype.map.call(buf, function (b) { return ('0' + b.toString(16)).slice(-2); }).join('');
    ls(function (s) { s.setItem(GUEST_KEY, id); });
    return id;
  }
  function num(v) { var n = Number(v); return isFinite(n) && n > 0 ? n : 0; }

  /* ---------- cache ---------- */
  function readCache() {
    var c = ls(function (s) { return JSON.parse(s.getItem(CACHE_KEY) || 'null'); });
    if (!c || !Array.isArray(c.items) || c.who !== WHO) return;
    var v = typeof c.version === 'number' ? c.version : -1;
    /* another tab's copy of the same list that is older than what this tab has drawn: not drawn */
    if (c.list && c.list === seenList && v < seenVersion) return;
    if (c.list && c.list !== seenList) { seenList = c.list; seenVersion = -1; seenConfirmed = false; }
    if (v > seenVersion) seenVersion = v;
    seenWho = WHO_SERVER; /* the copy is this shopper's: only a real change of who or list resets the order */
    lastKeys = c.items.map(function (r) { return r.key; });
    lastItems = c.items;
    state.items = overlay(visible(c.items)); state.count = state.items.length; state.shared_token = c.shared_token || null;
  }
  function writeCache() {
    if (foreign) return;
    ls(function (s) {
      var c = null;
      try { c = JSON.parse(s.getItem(CACHE_KEY) || 'null'); } catch (e) { c = null; }
      /* never overwrite another tab's newer copy of the same list */
      if (c && c.who === WHO && c.list && c.list === seenList && typeof c.version === 'number' && c.version > seenVersion) return;
      s.setItem(CACHE_KEY, JSON.stringify({ who: WHO, list: seenList, version: seenVersion, items: state.items, shared_token: state.shared_token, t: Date.now() }));
    });
  }
  /* rows moved to the cart whose removal failed: {key: {t, who}}; hidden here, removal retried as the
     shopper who moved them. Every change re-reads the stored record first, so tabs do not overwrite
     each other's entries. */
  function readGone() {
    var g = ls(function (s) { return JSON.parse(s.getItem(GONE_KEY) || 'null'); }) || {};
    var cut = Date.now() - GONE_DAYS * 864e5;
    Object.keys(g).forEach(function (k) {
      if (typeof g[k] === 'number') g[k] = { t: g[k], who: null };
      if (!g[k] || !(g[k].t > cut)) delete g[k];
    });
    return g;
  }
  function writeGone(g) { ls(function (s) { if (Object.keys(g).length) s.setItem(GONE_KEY, JSON.stringify(g)); else s.removeItem(GONE_KEY); }); }
  function goneSet(key, base, list) { gone = readGone(); gone[key] = { t: Date.now(), who: WHO, base: base, list: list }; writeGone(gone); return gone[key].t; }
  /* with t: only the entry made at that moment (a newer move of the same line keeps its own) */
  function goneDel(key, t) { gone = readGone(); if (gone[key] && (t === undefined || gone[key].t === t)) { delete gone[key]; writeGone(gone); } }
  var gone = readGone();
  function mine(who) { return who === null || who === WHO || (who === 'g' && IS_CUSTOMER); }
  function visible(items) { return (items || []).filter(function (it) { return !gone[it.key]; }); }

  /* ---------- the shopper's latest action per saved line ----------
     Answers can arrive in a different order than the server applied the requests. For each saved cart
     line the latest action (save or remove) is recorded; when every request for that row from this tab
     has settled, the newest answer (lastKeys) must agree with it. If it does not (a removal that landed
     after a re-save, a save overtaken by an older removal) the action is sent once more. A request that
     failed outright drops its own action (the caller has already shown the failure); if a newer action
     is waiting, the check first asks for a fresh list, since the failed request's outcome is unknown. */
  var intents = {}, inflight = {}, lastKeys = [];
  /* the order of answers, per list; base for removals only once this page has an answer of its own */
  var seenVersion = -1, seenWho = null, seenList = null, seenConfirmed = false;
  function baseNow() { return seenConfirmed && seenVersion >= 0 ? seenVersion : undefined; }
  /* a removal's base and the list it was read from (the Worker uses base only on that list) */
  function withBase(body, base, list) { if (base !== undefined && base !== null) { body.base = base; if (list) body.list = list; } return body; }
  function serverHas(key) { return lastKeys.indexOf(key) !== -1; }
  function strictRefresh() { return api('/list').then(function (j) { setState(j); return true; }, function () { return false; }); }
  function cloneLine(p) {
    var o = {};
    ['product_id', 'handle', 'variant_id', 'source', 'quantity', 'properties'].forEach(function (k) { if (p[k] !== undefined) o[k] = p[k]; });
    return o;
  }
  function storeIntent(key, e, prevT) {
    ls(function (s) {
      var m = JSON.parse(s.getItem(INTENT_KEY) || 'null') || {};
      if (e) m[key] = { op: e.op, t: e.t, who: e.who, payload: e.payload };
      else if (m[key] && (prevT === undefined || m[key].t === prevT)) delete m[key];
      if (Object.keys(m).length) s.setItem(INTENT_KEY, JSON.stringify(m)); else s.removeItem(INTENT_KEY);
    });
  }
  function intend(key, op, payload) {
    var e = { op: op, t: Date.now(), who: WHO, payload: payload ? cloneLine(payload) : null, retried: false };
    intents[key] = e;
    storeIntent(key, e);
    return e;
  }
  function settleIntent(key) { var e = intents[key]; delete intents[key]; if (e) storeIntent(key, null, e.t); }
  function begin(key) { inflight[key] = (inflight[key] || 0) + 1; }
  function end(key) { if (inflight[key] && !--inflight[key]) delete inflight[key]; if (!inflight[key]) reconcile(key); }
  function reconcile(key) {
    var e = intents[key];
    if (!e || inflight[key]) return;
    /* a request for this row failed with no answer: read a fresh list before comparing */
    if (e.unsure && !e.checked) {
      e.checked = true;
      begin(key);
      strictRefresh().then(function (ok) { if (!ok) e.keep = true; }).then(function () { end(key); });
      return;
    }
    /* a check that could not be made, or a re-send that failed: stays stored for the next page */
    if (e.keep) { delete intents[key]; return; }
    var present = serverHas(key);
    if ((e.op === 'save') === present || e.retried || (e.op === 'save' && !e.payload)) { settleIntent(key); return; }
    /* another tab has since recorded a newer action on this line: it is that tab's to check */
    var stored = (ls(function (s) { return JSON.parse(s.getItem(INTENT_KEY) || 'null'); }) || {})[key];
    if (stored && stored.t !== e.t) { delete intents[key]; return; }
    e.retried = true;
    begin(key);
    var body = e.op === 'save' ? cloneLine(e.payload) : { key: key };
    if (e.op === 'remove') withBase(body, baseNow(), seenList);
    api(e.op === 'save' ? '/items' : '/items/remove', body)
      .then(function (j) { if (e.op === 'save') goneDel(key); setState(j); }, function () { e.keep = true; refresh(); })
      .then(function () { end(key); });
  }
  /* a request for `key` failed: drop its own action, or make the newer one re-read before it is checked */
  function failed(key, own) {
    var cur = intents[key];
    if (cur && cur === own) settleIntent(key);
    else if (cur) cur.unsure = true;
  }
  /* a page left in the middle of a request: its action is checked against a fresh list on the next page */
  function checkStoredIntents(ok) {
    if (!ok) return; /* no fresh list: keep them for the next page */
    var m = ls(function (s) { return JSON.parse(s.getItem(INTENT_KEY) || 'null'); }) || {};
    var cut = Date.now() - 10 * 60 * 1000;
    Object.keys(m).forEach(function (key) {
      var e = m[key];
      if (!e || !(e.t > cut)) { storeIntent(key, null, e && e.t); return; }
      if (!mine(e.who) || intents[key] || inflight[key]) return;
      intents[key] = { op: e.op, t: e.t, who: e.who, payload: e.payload, retried: false };
      reconcile(key);
    });
  }
  /* the key the Worker gives a saved line: product:variant:cart:fnv1a(properties as JSON) */
  function cleanProps(props) {
    if (!props || typeof props !== 'object') return null;
    var o = {}, n = 0;
    Object.keys(props).forEach(function (k) { var v = props[k]; if (v == null) return; v = String(v); if (v === '') return; o[k] = v; n++; });
    return n ? o : null;
  }
  function lineKey(payload) {
    return payload.product_id + ':' + (payload.variant_id || 0) + ':cart:' + fnv(payload.properties ? JSON.stringify(payload.properties) : '');
  }

  /* this page's changes still on their way: kept on top of every answer until their own answer lands */
  var pendingAdds = [], pendingOuts = [], lastItems = [];
  function overlay(items) {
    var out = items.filter(function (it) { return !pendingOuts.some(function (t) { return t(it); }); });
    pendingAdds.forEach(function (p) { if (!out.some(function (it) { return it.product_id === p.product_id && isHeart(it); })) out.unshift(p); });
    return out;
  }
  function drop(list, x) { var i = list.indexOf(x); if (i !== -1) list.splice(i, 1); }
  /* redraw from the newest answer with what is still pending (used when a request's own answer was
     older than one already drawn, so setState did not redraw) */
  function restate() { state.items = overlay(visible(lastItems)); state.count = state.items.length; emit(); }

  function setState(body) {
    if (!body || !body.ok) return false;
    if (body.merged) ls(function (s) { s.removeItem(GUEST_KEY); });
    /* an answer older than one already drawn for the same list is not drawn; another list (a login, a
       replaced guest list) starts a new order */
    if ((body.who !== undefined && body.who !== seenWho) || (body.list && body.list !== seenList)) seenVersion = -1;
    if (body.who !== undefined) seenWho = body.who;
    if (body.list) seenList = body.list;
    if (typeof body.version === 'number') { if (body.version < seenVersion) return false; seenVersion = body.version; }
    seenConfirmed = true;
    lastKeys = (body.items || []).map(function (r) { return r.key; });
    if (body.who ? body.who !== WHO_SERVER : !!body.customer !== IS_CUSTOMER) foreign = true;
    lastItems = body.items || [];
    state.items = overlay(visible(lastItems));
    state.count = state.items.length;
    state.customer = !!body.customer;
    state.shared_token = body.shared_token || null;
    state.loaded = true;
    writeCache();
    emit();
    return true;
  }
  function emit() {
    paintAll();
    writeLines();
    listeners.forEach(function (fn) { try { fn(state); } catch (e) {} });
    try { document.dispatchEvent(new CustomEvent('fe:wishlist:change', { detail: state })); } catch (e) {}
  }
  /* another tab changed the list: take its copy (the server is the source of truth on next load) */
  window.addEventListener('storage', function (e) {
    if (e.key === LINES_KEY) { mergeViews(); return; }
    if (e.key !== CACHE_KEY && e.key !== GONE_KEY) return;
    gone = readGone();
    mergeViews();
    if (e.key === CACHE_KEY) readCache();
    else { state.items = visible(state.items); state.count = state.items.length; }
    emit();
  });

  /* ---------- API ---------- */
  function api(path, body) {
    var guest = guestId(!!body);
    var url = ENDPOINT + path;
    var opts = { credentials: 'same-origin', headers: { 'Accept': 'application/json' } };
    if (body) {
      opts.method = 'POST';
      opts.headers['Content-Type'] = 'application/json';
      body.guest = guest || undefined;
      opts.body = JSON.stringify(body);
      opts.keepalive = JSON.stringify(body).length < 60000; /* a save survives the page reloading under it */
    } else {
      url += (url.indexOf('?') === -1 ? '?' : '&') + 'guest=' + encodeURIComponent(guest || '');
    }
    return fetch(url, opts).then(function (r) {
      return r.json().catch(function () { return null; }).then(function (j) {
        if (!r.ok || !j || !j.ok) {
          var err = new Error((j && j.error) || ('request_failed_' + r.status));
          err.status = r.status; err.body = j;
          throw err;
        }
        return j;
      });
    });
  }

  function refresh() {
    if (!IS_CUSTOMER && !guestId(false)) { state.loaded = true; state.items = []; state.count = 0; writeCache(); emit(); return Promise.resolve(state); }
    return api('/list').then(function (j) { setState(j); return state; }).catch(function () { state.loaded = true; emit(); return state; });
  }

  function isHeart(it) { return (it.source || 'heart') === 'heart'; }
  /* a failed call puts back only the rows it took out, where they were, unless they came back another way */
  function putBack(removed) {
    removed.forEach(function (r) {
      if (gone[r.it.key] || state.items.some(function (x) { return x.key === r.it.key; })) return;
      state.items.splice(Math.min(r.i, state.items.length), 0, r.it);
    });
    state.count = state.items.length; emit();
  }
  function takeOut(test) {
    var removed = [];
    state.items.forEach(function (it, i) { if (test(it)) removed.push({ it: it, i: i }); });
    if (removed.length) { state.items = state.items.filter(function (it) { return !test(it); }); state.count = state.items.length; }
    return removed;
  }
  function has(productId) {
    var pid = num(productId);
    return state.items.some(function (it) { return it.product_id === pid && isHeart(it); });
  }
  function find(productId) {
    var pid = num(productId);
    return state.items.filter(function (it) { return it.product_id === pid; });
  }

  /* item: { product_id, handle, variant_id?, source? ('heart' | 'cart'), quantity?, properties? }
     meta: { title, price } for analytics (a saved cart line sends none); for a cart line, meta.view is
     the cart's own data for the line (title, url, image, vprice, variant, properties, pfp) so the strips
     can draw it at once, and meta.onSaved(row) hears the saved row before anything is redrawn. Rejects,
     with what it changed put back, when the save did not happen. */
  function add(item, meta) {
    meta = meta || {};
    var source = item.source === 'cart' ? 'cart' : 'heart';
    var payload = { product_id: num(item.product_id), handle: String(item.handle || ''), variant_id: num(item.variant_id) || null, source: source };
    var pendingRow = null, key = null, intent = null;
    if (source === 'cart') {
      payload.quantity = Math.min(100, Math.max(1, num(item.quantity) || 1));
      var props = cleanProps(item.properties);
      if (props) payload.properties = props;
      key = lineKey(payload);
      intent = intend(key, 'save', payload);
      begin(key);
    }
    if (source === 'heart' && !has(payload.product_id)) {
      pendingRow = { key: payload.product_id + ':' + (payload.variant_id || 0) + ':heart:pending', product_id: payload.product_id, variant_id: payload.variant_id, source: 'heart', handle: payload.handle, added_at: Date.now(), quantity: 1, properties: null, pending: true };
      pendingAdds.push(pendingRow);
      state.items.unshift(pendingRow);
      state.count = state.items.length; emit();
    }
    return api('/items', payload).then(function (j) {
      if (source === 'cart') {
        var row = null;
        (j.items || []).forEach(function (it) { if (it.key === key) row = it; });
        /* the answer can already be overtaken by a removal of this row: the line is drawn from what was
           saved, and the check after the last request for it saves it again if needed */
        if (!row) row = { key: key, product_id: payload.product_id, variant_id: payload.variant_id || 0, source: 'cart', handle: payload.handle, quantity: payload.quantity, properties: payload.properties || null };
        /* this exact line saved again after a failed move: show it again */
        gone = readGone();
        if (gone[key]) goneDel(key);
        if (meta.view) views[key] = viewFromCart(meta.view, row);
        if (meta.onSaved) { try { meta.onSaved(row); } catch (e) {} }
      }
      if (pendingRow) drop(pendingAdds, pendingRow);
      if (!setState(j) && pendingRow) restate();
      if (source === 'cart') end(key);
      if (source === 'heart') track('add', payload, meta);
      return state;
    }).catch(function (err) {
      if (source === 'cart') { failed(key, intent); end(key); }
      if (pendingRow) { drop(pendingAdds, pendingRow); state.items = state.items.filter(function (x) { return x !== pendingRow; }); state.count = state.items.length; emit(); }
      refresh();
      throw err;
    });
  }
  /* un-heart: removes this product's heart rows (one variant when given); saved cart lines stay */
  function remove(productId, variantId, meta) {
    var pid = num(productId);
    var payload = { product_id: pid, source: 'heart' };
    if (variantId !== undefined) payload.variant_id = num(variantId) || null;
    withBase(payload, baseNow(), seenList);
    var test = function (it) { return it.product_id === pid && isHeart(it) && (variantId === undefined || (it.variant_id || 0) === (num(variantId) || 0)); };
    var removed = takeOut(test);
    pendingOuts.push(test);
    emit();
    /* the event names the product as the add did: handle, and the size the heart was saved at */
    var first = removed.length ? removed[0].it : null;
    var trackRow = { product_id: pid, variant_id: first ? first.variant_id || null : null, handle: (first && first.handle) || (meta && meta.handle) || '' };
    return api('/items/remove', payload).then(function (j) {
      drop(pendingOuts, test);
      if (!setState(j)) restate();
      if (j.removed !== 0) track('remove', trackRow, meta);
      return state;
    }).catch(function (err) { drop(pendingOuts, test); putBack(removed); refresh(); throw err; });
  }
  /* one row, by its server key (the wishlist page and the strips). opts.meta: { title, price } for the
     analytics of a heart row. opts.moved: the row has just gone into the cart, so a failed removal keeps
     it out of view (here and on later pages) and retries, instead of offering it again. */
  function removeLine(key, opts) {
    opts = opts || {};
    var test = function (it) { return it.key === key; };
    var removed = takeOut(test);
    var row = removed.length ? removed[0].it : null;
    pendingOuts.push(test);
    var t0, isLine = String(key).split(':')[2] === 'cart', intent = null;
    var base = baseNow(), body = withBase({ key: key }, base, seenList);
    if (opts.moved) t0 = goneSet(key, base, seenList);
    if (isLine) { intent = intend(key, 'remove'); begin(key); }
    emit();
    return api('/items/remove', body).then(function (j) {
      if (opts.moved) goneDel(key, t0);
      /* kept: the row was saved again after the view this removal was based on; that later save wins */
      if (isLine && j.kept > 0 && intents[key] === intent) settleIntent(key);
      drop(pendingOuts, test);
      if (!setState(j)) restate();
      if (isLine) end(key);
      var n = j.removed;
      /* the analytics need the row; when this page's copy was out of date (a size move that failed a
         moment ago), the key itself says it was a heart; only a row the server really deleted counts */
      var parts = String(key).split(':');
      if (!row && parts[2] === 'heart' && n > 0) row = { product_id: num(parts[0]), variant_id: num(parts[1]) || null, handle: '' };
      if (row && isHeart(row) && n !== 0) track('remove', row, opts.meta);
      return { removed: n === undefined ? null : n };
    }).catch(function (err) {
      drop(pendingOuts, test);
      if (isLine) { failed(key, intent); end(key); }
      if (opts.moved) setTimeout(flushGone, 2500);
      else { putBack(removed); refresh(); }
      throw err;
    });
  }
  /* retries the removals of moved rows, each only as the shopper who moved it (a guest's rows were
     merged into the customer's list at login, so a customer also retries guest entries) */
  function flushGone() {
    gone = readGone();
    Object.keys(gone).forEach(function (key) {
      var entry = gone[key];
      if (inflight[key] || !mine(entry.who)) return;
      begin(key);
      var body = withBase({ key: key }, typeof entry.base === 'number' ? entry.base : undefined, entry.list);
      api('/items/remove', body)
        .then(function (j) {
          /* cleared once the row is gone, or was kept because it was saved again later; otherwise the
             line stays hidden and is retried on a later page */
          if (j.kept > 0 || !(j.items || []).some(function (it) { return it.key === key; })) goneDel(key, entry.t);
          setState(j);
        }, function () {})
        .then(function () { end(key); });
    });
  }
  function changeVariant(key, toVariantId) {
    var to = num(toVariantId);
    return api('/items/variant', { key: key, to_variant_id: to }).then(function (j) { setState(j); return j.moved_key; }).catch(function (err) { refresh(); throw err; });
  }
  function clear() {
    state.items = []; state.count = 0; emit();
    return api('/clear', {}).then(function (j) { setState(j); return state; });
  }
  function share() {
    return api('/share', {}).then(function (j) { setState(j); return j.token; });
  }
  function importItems(items, opts) {
    if (!items.length && !(opts && opts.swish)) return Promise.resolve({ imported: 0 });
    var body = { items: items };
    if (opts && opts.swish) body.swish = true;
    return api('/import', body).then(function (j) { setState(j); return j; });
  }
  function productUrl(item) { return '/products/' + encodeURIComponent(item.handle) + (item.variant_id ? '?variant=' + item.variant_id : ''); }

  /* ---------- money in the shopper's currency ---------- */
  function money(cents) {
    if (cents == null || isNaN(cents)) return '';
    var active = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    var shop = (window.Shopify && Shopify.currency && Shopify.currency.shop) || 'USD';
    if (active === shop || !window.Intl) {
      var fmt = (window.themeVariables && themeVariables.settings && themeVariables.settings.moneyFormat) || '${{amount}}';
      var text = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return fmt.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, text).replace(/<[^>]+>/g, '');
    }
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: active }).format(cents / 100); } catch (e) { return (cents / 100).toFixed(2) + ' ' + active; }
  }

  /* ---------- analytics (the event Swish pushed, same shape, for GTM) ---------- */
  function track(kind, payload, meta) {
    meta = meta || {};
    var title = meta.title || '';
    var price = Number(meta.price);
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    if (kind === 'add') {
      try {
        window.dataLayer = window.dataLayer || [];
        var it = { item_id: payload.product_id, item_name: title, affiliation: 'Foreverings Wishlist', item_category: '', item_brand: 'FOREVERINGS', item_quantity: 1 };
        var ec = { currency: currency, items: [it] };
        if (isFinite(price) && price > 0) { ec.value = price; it.item_price = price; }
        if (payload.variant_id) it.item_variant = String(payload.variant_id);
        window.dataLayer.push({ event: 'add_to_wishlist', ecommerce: ec });
      } catch (e) {}
    }
    try {
      var k = window.klaviyo || window._learnq;
      if (k && typeof k.push === 'function') {
        k.push(['track', kind === 'add' ? 'Added product to wishlist' : 'Removed product from wishlist', { ProductID: payload.product_id, VariantID: payload.variant_id || null, ProductName: title, Handle: payload.handle, URL: payload.handle ? location.origin + '/products/' + payload.handle : undefined }]);
      }
    } catch (e) {}
  }

  /* ---------- hearts ---------- */
  function heartVariant(btn) {
    var choiceSel = btn.getAttribute('data-fe-choice'), syncSel = btn.getAttribute('data-fe-sync');
    var choice = choiceSel ? document.querySelector(choiceSel) : null;
    var input = syncSel ? document.querySelector(syncSel) : null;
    if (choice) {
      /* a size picker exists: nothing is bound until it holds a value (the hidden input is
         pre-filled with the first variant and is not evidence of a choice) */
      if (!choice.value) return 0;
      return input && input.value ? num(input.value) : num(btn.getAttribute('data-variant-id'));
    }
    /* no size picker on this page (single-variant products, or a form without one) */
    if (input && input.value && !choiceSel) return num(input.value);
    return num(btn.getAttribute('data-variant-id'));
  }
  function setIf(el, attr, value) { if (el.getAttribute(attr) !== value) el.setAttribute(attr, value); }
  function paint(btn) {
    var on = has(btn.getAttribute('data-product-id'));
    if (btn.classList.contains('is-on') !== on) btn.classList.toggle('is-on', on);
    setIf(btn, 'aria-pressed', on ? 'true' : 'false');
    var label = btn.getAttribute('data-label') || '';
    setIf(btn, 'aria-label', (on ? 'Remove ' : 'Save ') + (label ? label + ' ' : '') + (on ? 'from your wishlist' : 'to your wishlist'));
  }
  function paintAll() {
    var hearts = document.querySelectorAll('[data-fe-wl-heart]');
    for (var i = 0; i < hearts.length; i++) paint(hearts[i]);
    paintHeader();
  }
  /* writes only what changed: a text write is a DOM mutation, and the observer below would
     otherwise repaint every 60 ms forever */
  function paintHeader() {
    var links = document.querySelectorAll('.wishlist-header-link .wkh-button, [data-fe-wl-link]');
    var n = String(state.count);
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      var counter = a.querySelector('.wkh-counter, [data-fe-wl-count]');
      if (counter && counter.textContent !== n) counter.textContent = n;
      if (a.classList.contains('wkh-selected') !== (state.count > 0)) a.classList.toggle('wkh-selected', state.count > 0);
      setIf(a, 'aria-label', state.count ? 'Wishlist, ' + state.count + (state.count === 1 ? ' item' : ' items') : 'Wishlist');
    }
  }
  function onHeartClick(e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-fe-wl-heart]') : null;
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var pid = btn.getAttribute('data-product-id'), handle = btn.getAttribute('data-handle');
    if (!pid || !handle || btn.classList.contains('is-busy')) return;
    btn.classList.add('is-busy');
    var done = function () { btn.classList.remove('is-busy'); };
    var meta = { title: btn.getAttribute('data-title') || btn.getAttribute('data-label') || '', price: btn.getAttribute('data-price'), handle: handle };
    if (has(pid)) {
      remove(pid, undefined, meta).then(done, done);
    } else {
      btn.classList.add('is-pop');
      setTimeout(function () { btn.classList.remove('is-pop'); }, 450);
      var vid = heartVariant(btn);
      /* on a product page the chosen size and width set the price (GA value), as Swish reported it */
      try {
        var mp = window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product;
        if (vid && mp && String(mp.id) === String(pid) && mp.variants) mp.variants.forEach(function (v) { if (v && v.id === vid && v.price != null) meta.price = v.price / 100; });
      } catch (x) {}
      add({ product_id: pid, handle: handle, variant_id: vid, source: 'heart' }, meta).then(done, done);
    }
  }
  document.addEventListener('click', onHeartClick, true);

  /* hearts that arrive later (carousels, tab templates, drawer refreshes, Load more) */
  var mo;
  function watch() {
    if (mo || !window.MutationObserver || !document.body) return;
    var pending = false;
    mo = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      setTimeout(function () { pending = false; paintAll(); }, 60);
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  /* ---------- products (one fetch per handle per page) ---------- */
  var productCache = {};
  function fetchProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
        .then(function (r) { if (r.status === 404) return null; if (!r.ok) throw new Error('product_' + r.status); return r.json(); })
        .catch(function (err) { delete productCache[handle]; throw err; });
    }
    return productCache[handle];
  }
  function optionNames(p) { return (p.options || []).map(function (o) { return typeof o === 'string' ? o : (o && o.name) || ''; }); }

  /* ---------- the cart "Saved for later" strips (cart page + drawer) ----------
     Only lines saved from the cart, newest first, as the old browser-only strip showed them.
     views: row key -> the drawn line (product data as last seen), kept in localStorage for first paint. */
  var STRIP_MAX = 20;
  var views = {}, dropped = {};
  /* lines drawn by any tab for this shopper (localStorage) join this tab's drawings */
  function mergeViews() {
    var snap = ls(function (s) { return JSON.parse(s.getItem(LINES_KEY) || 'null'); });
    if (snap && snap.who === WHO && Array.isArray(snap.lines)) snap.lines.forEach(function (l) { if (l && l.key && !views[l.key] && !dropped[l.key]) views[l.key] = l; });
  }
  mergeViews();
  function stripRows(limit) { return state.items.filter(function (it) { return it.source === 'cart' && it.variant_id; }).slice(0, limit || STRIP_MAX); }
  /* the strip from what is known right now (no network): rows whose line has been drawn before */
  function cartLinesNow(limit) {
    var lines = stripRows(limit).map(function (it) {
      var v = views[it.key];
      if (!v) return null;
      var o = {};
      for (var k in v) if (Object.prototype.hasOwnProperty.call(v, k)) o[k] = v[k];
      o.quantity = it.quantity || 1;
      return o;
    }).filter(Boolean);
    return { lines: lines, total: state.count, more: Math.max(0, state.count - lines.length), url: ENDPOINT };
  }
  function writeLines() {
    if (foreign) return;
    mergeViews();
    var out = cartLinesNow(STRIP_MAX);
    out.who = WHO;
    ls(function (s) { s.setItem(LINES_KEY, JSON.stringify(out)); });
  }
  /* a line just saved from the cart, drawn from the cart's own data until the network version replaces it */
  function viewFromCart(d, row) {
    var props = {};
    var src = d.properties && !Array.isArray(d.properties) ? d.properties : {};
    Object.keys(src).forEach(function (k) { if (k.indexOf('__') !== 0 && src[k] != null && src[k] !== '') props[k] = String(src[k]); });
    return {
      key: row.key, product_id: row.product_id, variant_id: row.variant_id, handle: row.handle,
      id: row.variant_id, quantity: row.quantity || 1, properties: props, pfp: num(d.pfp) || null,
      title: d.title || '', variant: d.variant || '', url: d.url || ('/products/' + encodeURIComponent(row.handle) + '?variant=' + row.variant_id),
      image: d.image || '', price: d.vprice != null && d.vprice !== '' ? money(num(d.vprice)) : (d.price || ''), available: true
    };
  }
  function cartLines(limit) {
    limit = limit || STRIP_MAX;
    return ready.then(function () {
      var picked = stripRows(limit);
      return Promise.all(picked.map(function (it) {
        return fetchProduct(it.handle).catch(function () { return views[it.key] ? 'keep' : null; }).then(function (p) {
          if (p === 'keep') { views[it.key].quantity = it.quantity || 1; return views[it.key]; } /* the product could not be fetched just now: keep the last drawing */
          if (!p) { delete views[it.key]; dropped[it.key] = 1; return null; }
          var v = null;
          for (var i = 0; i < p.variants.length; i++) if (p.variants[i].id === it.variant_id) v = p.variants[i];
          if (!v) { delete views[it.key]; dropped[it.key] = 1; return null; }
          var props = {}, pfp = null;
          Object.keys(it.properties || {}).forEach(function (k) { if (k === '__pfp') pfp = Number(it.properties[k]) || null; else if (k.indexOf('__') !== 0) props[k] = it.properties[k]; });
          var names = optionNames(p);
          var sized = !(p.variants.length === 1 && (!names.length || /^title$/i.test(names[0])));
          var image = (v.featured_image && v.featured_image.src) || p.featured_image || (p.images && p.images[0]) || '';
          if (image) image = image.replace(/(\?|&)width=\d+/, '') + (image.indexOf('?') === -1 ? '?' : '&') + 'width=192';
          return {
            key: it.key, product_id: it.product_id, variant_id: it.variant_id, handle: it.handle,
            id: v.id, quantity: it.quantity || 1, properties: props, pfp: pfp,
            title: p.title, variant: sized ? names.map(function (name, idx) { return name + ': ' + v.options[idx]; }).join(' · ') : '',
            url: '/products/' + encodeURIComponent(p.handle) + '?variant=' + v.id,
            image: image, price: money(v.price), available: v.available
          };
        });
      })).then(function (lines) {
        /* a line that left the list (moved, removed, another tab) while products loaded is not drawn again */
        var live = {};
        stripRows(limit).forEach(function (it) { live[it.key] = 1; });
        lines = lines.filter(function (l) { return l && live[l.key]; });
        lines.forEach(function (l) { views[l.key] = l; delete dropped[l.key]; });
        writeLines();
        return { lines: lines, total: state.count, more: Math.max(0, state.count - lines.length), url: ENDPOINT };
      });
    });
  }

  /* ---------- one-time imports (each marked done only when it worked) ---------- */
  /* fe-saved-later stays as it is (a rollback to the old theme still shows those lines); the mark keeps
     the keys already copied, so a line the old theme added later is copied on the next visit, and a
     line copied once is never copied again (even after the shopper removes it here). The old mark '1'
     came with the old list already deleted, so any lines there now are new. */
  function oldLineKey(d) { return String(d.key || (d.id + ':' + JSON.stringify(d.properties || {}))); }
  function importOldSavedLines() {
    var mark = ls(function (s) { return s.getItem(IMP_SAVED); });
    var done = {};
    if (mark && mark !== '1') { try { (JSON.parse(mark) || []).forEach(function (k) { done[k] = 1; }); } catch (e) {} }
    var old = ls(function (s) { return JSON.parse(s.getItem(OLD_SAVED) || 'null'); });
    var fresh = Array.isArray(old) ? old.filter(function (d) { return d && !done[oldLineKey(d)]; }).slice(0, 50) : [];
    if (!fresh.length) return Promise.resolve();
    /* newest first, as the old strip kept them */
    var jobs = fresh.map(function (d) {
      var m = /\/products\/([^?#/]+)/.exec(String(d.url || ''));
      if (!m) return Promise.resolve(null);
      return fetchProduct(decodeURIComponent(m[1])).then(function (p) {
        if (!p) return null; /* the product is gone */
        var props = {};
        var src = Array.isArray(d.properties) ? {} : (d.properties || {});
        Object.keys(src).forEach(function (k) { if (src[k] != null && src[k] !== '') props[k] = String(src[k]); });
        if (d.pfp) props.__pfp = String(d.pfp);
        return { product_id: p.id, variant_id: num(d.id) || null, handle: p.handle, source: 'cart', quantity: Math.min(100, Math.max(1, num(d.quantity) || 1)), properties: Object.keys(props).length ? props : undefined };
      });
    });
    return Promise.all(jobs).then(function (items) {
      return importItems(items.filter(Boolean)).then(function () {
        fresh.forEach(function (d) { done[oldLineKey(d)] = 1; });
        ls(function (s) { s.setItem(IMP_SAVED, JSON.stringify(Object.keys(done))); });
      });
    }).catch(function () { /* network or server trouble: try again on the next page */ });
  }
  function importSwish() {
    if (!cfg.import_swish) return Promise.resolve();
    if (ls(function (s) { return s.getItem(IMP_SWISH); }) === '1') return Promise.resolve();
    /* a customer's copy already ran in this browser: a later logged-out copy would only bring back,
       through the next login's merge, hearts that customer has since removed */
    if (!IS_CUSTOMER && ls(function (s) { for (var i = 0; i < s.length; i++) { var k = s.key(i); if (k && k.indexOf('fe-wl-imp-swish:c') === 0) return true; } return false; })) {
      ls(function (s) { s.setItem(IMP_SWISH, '1'); });
      return Promise.resolve();
    }
    return new Promise(function (resolve) {
      var tries = 0;
      (function wait() {
        var wk = window.WishlistKing;
        var api2 = wk && wk._apis && wk._apis.wishlist;
        if (!api2) { if (++tries > 60) { resolve(); return; } setTimeout(wait, 250); return; } /* SDK not here (yet): retry next page */
        if (!(api2.hasSession || api2.hasCustomer)) { ls(function (s) { s.setItem(IMP_SWISH, '1'); }); resolve(); return; } /* never used Swish */
        api2.loadWishlist({ wishlistId: 'mine' }).then(function (r) {
          var w = r && r.wishlist;
          var items = ((w && w.items) || []).map(function (it) {
            var p = it.product || {};
            if (!p.id || !p.handle) return null;
            return { product_id: p.id, variant_id: num(it.variantId) || null, handle: p.handle, source: 'heart' };
          }).filter(Boolean);
          return importItems(items, { swish: true }).then(function () { ls(function (s) { s.setItem(IMP_SWISH, '1'); }); });
        }).catch(function () {}).then(resolve);
      })();
    });
  }

  /* ---------- boot ---------- */
  readCache();
  var ready = new Promise(function (resolve) {
    function start() {
      paintAll();
      watch();
      refresh().then(function () {
        resolve(state);
        if (Object.keys(gone).length) flushGone();
        if (ls(function (s) { return s.getItem(INTENT_KEY); })) setTimeout(function () { strictRefresh().then(checkStoredIntents); }, 4000);
        importOldSavedLines().then(importSwish);
      });
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  });
  document.addEventListener('cart-drawer:refreshed', paintAll);

  window.FEWishlist = {
    config: cfg, endpoint: ENDPOINT, ready: ready, state: state,
    has: has, find: find, add: add, remove: remove, removeLine: removeLine, changeVariant: changeVariant,
    clear: clear, share: share, refresh: refresh, importItems: importItems, productUrl: productUrl,
    fetchProduct: fetchProduct, optionNames: optionNames, cartLines: cartLines, cartLinesNow: cartLinesNow, money: money,
    onChange: function (fn) { listeners.push(fn); return function () { listeners = listeners.filter(function (f) { return f !== fn; }); }; },
    paint: paintAll,
    /* whether the newest answer from the server lists this row (not this page's guess) */
    serverHas: serverHas
  };
})();
